import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { trackEvent } from '@/lib/analytics';

import { getApiUrl } from '@/lib/apiUrl';
import { attachErrorNormalizer, errorText } from '@/lib/apiErrors';

const BACKEND_URL = getApiUrl();
const API = `${BACKEND_URL}/api`;

// A validation failure (422) carries `detail` as a list of objects, and pages
// print `detail` as it comes — a list there takes the whole app down. Every
// client turns it into a sentence first (see lib/apiErrors). This line covers
// the pages that call axios directly (sign-in, elderly portal, shared
// passport); clients made with axios.create() attach it themselves.
attachErrorNormalizer(axios);

// NOTE: Do NOT set axios.defaults.withCredentials — it breaks CORS on production
// where the ingress proxy sets Access-Control-Allow-Origin: *. Auth uses Bearer tokens.

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// docs/41 P0-1 (founder Q2): the login consent prompt's per-session skip
// marker. Cleared on every fresh sign-in so each login re-asks for any
// still-undecided consents; lives here (not in the component) so login()
// can clear it without a circular import.
export const CONSENT_SKIP_KEY = 'consent_login_prompt_skip';

// Can the mount probe (GET /auth/me) come back signed in? Only if it carries
// a credential, and this client has two: the Bearer token kept in
// localStorage, and the server's session cookie — which the browser attaches
// only when the API is on the page's own origin, because this client does not
// send credentials cross-origin (see the note at the top of this file).
//
// So on the split-origin deployment (nsofar.com -> api.nsofar.com) a visitor
// with no stored token is known to be signed out before the probe is even
// sent, and the logged-out pages are drawn at once instead of after a round
// trip to the API. Where the API shares the page's origin a cookie alone can
// sign the visitor in, so there the app waits for the probe as it always did:
// nobody is shown the landing page and then moved to the dashboard.
//
// Anything unclear answers "maybe" — wait for the probe. If cross-origin
// credentials are ever switched on for this client, the withCredentials
// check below turns the shortcut off rather than letting it go wrong.
function probeCanSignIn(token, api) {
  if (token) return true;
  if (api.defaults.withCredentials || axios.defaults.withCredentials) return true;
  try {
    return new URL(api.defaults.baseURL, window.location.href).origin === window.location.origin;
  } catch {
    return true;
  }
}

// How long the app stays blank for an expired session's redirect to the
// sign-in page before giving up on it (see the mount probe below).
const EXPIRED_REDIRECT_GRACE_MS = 5000;

// The longest a fresh sign-in waits for its gates (see addSignInGate) before
// the person is let in anyway.
const SIGN_IN_GATE_LIMIT_MS = 4000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [needsReConsent, setNeedsReConsent] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(null);
  // In-memory token for Bearer header — also persisted to localStorage
  const tokenRef = useRef(localStorage.getItem('token'));
  // True once the 401 interceptor has sent the browser to /auth?expired=1.
  const leavingRef = useRef(false);

  const createApi = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    attachErrorNormalizer(instance);
    // Attach Bearer token to every request — primary auth mechanism
    instance.interceptors.request.use(config => {
      if (tokenRef.current) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    // Intercept 401/402/403.
    instance.interceptors.response.use(null, async (err) => {
      // 401 on an authenticated request => the token is dead (expired or
      // revoked server-side via tokens_valid_after). Clear it and bounce to
      // login so a revoked session degrades gracefully instead of showing
      // errors until the next manual reload (audit §10 #10 / docs/37 P1).
      //   - Skip when we never had a token: an anonymous request 401ing is the
      //     normal logged-out state (e.g. the mount /auth/me probe), not an
      //     expiry — redirecting a public visitor to login would be wrong.
      //   - Skip login/register/logout: a 401 there is a bad-credentials
      //     response the form handles, or logout already doing its own redirect.
      const reqUrl = err.config?.url || '';
      const skip401 = /\/auth\/(login|register|logout)/.test(reqUrl);
      if (err.response?.status === 401 && tokenRef.current && !skip401) {
        tokenRef.current = null;
        localStorage.removeItem('token');
        if (window.location.pathname !== '/auth') {
          leavingRef.current = true;
          window.location.href = '/auth?expired=1';
        }
        return Promise.reject(err);
      }
      // Both branches below need the reason as TEXT: the first is printed by the
      // upgrade dialog, the second is searched. Newer endpoints answer 402 and
      // 403 with an object ({ error, message, ... }), not a sentence.
      if (err.response?.status === 402) {
        const detail = errorText(err, '');
        setUpgradeModal({
          serviceName: detail,
          featureKey: err.config?.url?.split('/')?.[1] || '',
          is403: false,
        });
      }
      if (err.response?.status === 403) {
        const detail = errorText(err, '');
        if (detail.toLowerCase().includes('coming soon')) {
          setUpgradeModal({ serviceName: detail, is403: true, comingSoonMessage: detail });
        }
      }
      return Promise.reject(err);
    });
    return instance;
  }, []);

  // Stable api instance
  const apiRef = useRef(null);
  if (!apiRef.current) {
    apiRef.current = createApi();
  }

  const getToken = useCallback(() => tokenRef.current, []);

  // Sign-in gates: work that starts the moment a sign-in has its token and
  // must be done before the person is shown as signed in. The signed-in app
  // is not drawn until it knows what the account may open (see
  // FeatureAccessContext); asking for that after `user` is set left the
  // screen empty for one more round trip, between the sign-in form and the
  // app. Asked from here, the form's own "signing in" state covers the wait
  // and the app is drawn in one step. A gate gets the account that is
  // signing in; one that fails or is slow never keeps anybody out.
  const signInGatesRef = useRef(new Set());
  const addSignInGate = useCallback((gate) => {
    signInGatesRef.current.add(gate);
    return () => { signInGatesRef.current.delete(gate); };
  }, []);
  const passSignInGates = (userData) => {
    const gates = [...signInGatesRef.current].map(
      gate => Promise.resolve().then(() => gate(userData)).catch(() => {}),
    );
    return Promise.race([
      Promise.all(gates),
      new Promise(resolve => setTimeout(resolve, SIGN_IN_GATE_LIMIT_MS)),
    ]);
  };

  // `loading` holds every route back until the first auth check has answered.
  // It starts out false when the answer is already known: see probeCanSignIn.
  const [loading, setLoading] = useState(() => probeCanSignIn(tokenRef.current, apiRef.current));

  // On mount: check if we're already authenticated (cookie or stored token)
  useEffect(() => {
    apiRef.current.get('/auth/me')
      .then(res => {
        setUser(res.data);
        // Check consent text versions for re-consent flag (Section 9.3)
        checkReConsent(res.data);
      })
      .catch(() => { setUser(null); })
      .finally(() => {
        // An expired token is already on its way to /auth?expired=1 (the 401
        // interceptor above). Keep the routes held back until that navigation
        // lands: letting go now would draw the logged-out page for the moment
        // in between — on "/", the landing page — before the sign-in page
        // replaces it. The timer only matters if the navigation never
        // happens (cancelled by the visitor): the app must not stay blank.
        if (leavingRef.current) setTimeout(() => setLoading(false), EXPIRED_REDIRECT_GRACE_MS);
        else setLoading(false);
      });
  }, []);

  const checkReConsent = async (userData) => {
    if (!userData?.id) return;
    try {
      const [statusRes, textsRes] = await Promise.all([
        apiRef.current.get(`/consent/status/${userData.id}`),
        apiRef.current.get('/consent/texts').catch(() => ({ data: [] })),
      ]);
      const beneficiaries = statusRes.data?.beneficiaries || {};
      const texts = textsRes.data || [];
      const latestVersions = {};
      for (const t of texts) {
        if (!latestVersions[t.purpose_id] || t.version > latestVersions[t.purpose_id]) {
          latestVersions[t.purpose_id] = t.version;
        }
      }
      let mismatch = false;
      for (const purposes of Object.values(beneficiaries)) {
        for (const [pid, consent] of Object.entries(purposes)) {
          if (consent?.status === 'GRANTED' && latestVersions[pid] && consent.consent_text_version && consent.consent_text_version < latestVersions[pid]) {
            mismatch = true;
          }
        }
      }
      setNeedsReConsent(mismatch);
    } catch {
      // Silently fail
    }
  };

  const login = async (email, password, expectedRole = '') => {
    const res = await apiRef.current.post('/auth/login', { email, password, expected_role: expectedRole });
    if (res.data.token) {
      tokenRef.current = res.data.token;
      localStorage.setItem('token', res.data.token);
    }
    sessionStorage.removeItem(CONSENT_SKIP_KEY);
    await passSignInGates(res.data.user);
    setUser(res.data.user);
    trackEvent('login', { method: 'email', role: res.data.user?.role || expectedRole });
    return res.data;
  };

  // Resolves once the person is signed in. Await it before navigating: until
  // then the signed-in routes do not exist.
  const loginWithToken = async (token, userData) => {
    tokenRef.current = token;
    localStorage.setItem('token', token);
    sessionStorage.removeItem(CONSENT_SKIP_KEY);
    await passSignInGates(userData);
    setUser(userData);
    trackEvent('login', { method: userData?.auth_provider || 'social', role: userData?.role || 'user' });
  };

  const register = async (data) => {
    const res = await apiRef.current.post('/auth/register', data);
    if (res.data.token) {
      tokenRef.current = res.data.token;
      localStorage.setItem('token', res.data.token);
    }
    sessionStorage.removeItem(CONSENT_SKIP_KEY);
    await passSignInGates(res.data.user);
    setUser(res.data.user);
    trackEvent('sign_up', { method: 'email', role: res.data.user?.role || 'user' });
    return res.data;
  };

  const logout = async () => {
    try {
      await apiRef.current.post('/auth/logout');
    } catch {}
    tokenRef.current = null;
    localStorage.removeItem('token');
    setUser(null);
    // Redirect to the public landing page. Authed routes (e.g. /dashboard) are
    // only registered while `user` is set, so without this the user is stranded
    // on a now-unrouted path -> 404. Hard nav also fully clears in-memory state.
    window.location.href = '/';
  };

  const refreshUser = async () => {
    try {
      const res = await apiRef.current.get('/auth/me');
      setUser(res.data);
    } catch {}
  };

  // Poll user data every 30s to pick up admin plan changes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      apiRef.current.get('/auth/me').then(res => setUser(res.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, register, logout, api: apiRef.current, refreshUser, needsReConsent, upgradeModal, setUpgradeModal, getToken, addSignInGate }}>
      {children}
    </AuthContext.Provider>
  );
};
